import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import { EmPairingStatus, IemEventsPerSiteGQL, IemInfoPerSiteGQL, TimeFrame } from '@uigy/generated/graphql';
import { RISK_OFF_VALUE, RiskService } from '@uigy/shared/services/risk.service';
import { map } from 'rxjs/operators';
import { ColDef, ICellRendererParams, ITooltipParams } from '@ag-grid-community/core';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import {
  RiskScoreCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/risk-score-cell-renderer/risk-score-cell-renderer.component';
import { UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { UiGridIdsEnum } from '@uigy/shared/components/ui-grid/enums/ui-grid-ids.enum';
import { DefaultPathService } from '@uigy/shared/services/default-path.service';
import { TimeFrameService } from '@uigy/shared/services/time-frame.service';
import { forkJoin, Observable } from 'rxjs';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { ExternalLinkICellRendererParams } from '@uigy/shared/models/interfaces';
import {
  ExternalLinkRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/external-link-renderer/external-link-renderer.component';
import { IcpsService } from '@uigy/modules/icps/services/icps.service';

interface ConnectedSiteTableRow {
  name: string;
  machineId: string;
  status: EmPairingStatus;
  ave_risk: number;
  assets_count: number;
  vulnerabilities_count: number;
  events_count: number;
}


@Injectable({
  providedIn: 'root'
})
export class IemConnectedSitesGridSettingsService {
  private languageService = inject(LanguageService);
  private riskService = inject(RiskService);
  private iemInfoPerSiteGQL = inject(IemInfoPerSiteGQL);
  private iemEventsPerSiteGQL = inject(IemEventsPerSiteGQL);
  private defaultPathService = inject(DefaultPathService);
  private timeFrameService = inject(TimeFrameService);
  private icpsService = inject(IcpsService);


  public readonly texts = this.languageService.texts.overview.connected_sites;
  public readonly buttonIcon: IconsEnum = IconsEnum.network;
  public readonly buttonNavigateTo: string = `/${RoutesStatesNamesEnum.icps}`;

  private getCombinedData(): Observable<ConnectedSiteTableRow[]> {
    const yesterday = this.timeFrameService.timeFrameToDateTime(
      this.timeFrameService.getTimeFrameOption(TimeFrame.OneDay)
    );

    const siteInfo$ = this.iemInfoPerSiteGQL.fetch().pipe(
      map(res => ({
        groupedAssetsRaw: res.data.groupedAssetsRaw.nodes,
        emPairedIcps: res.data.emPairedIcps.nodes,
        iemPluginsRaw: res.data.iemPluginsRaw.nodes,
      }))
    );

    const events$ = this.iemEventsPerSiteGQL.fetch({startTime: yesterday}).pipe(
      map(res => res.data.groupedEventsRaw.nodes)
    );

    return forkJoin([siteInfo$, events$]).pipe(
      map(([siteInfo, events]) => {
        const {groupedAssetsRaw, emPairedIcps, iemPluginsRaw} = siteInfo;

        const siteDataMap = new Map();

        emPairedIcps.forEach(icp => {
          if (icp.status === EmPairingStatus.IcpConnected && !siteDataMap.has(icp.site.machineId)) {
            siteDataMap.set(icp.site.machineId,
              {
                'name': icp.site.name,
                'status': icp.status,
                'id': icp.site.machineId,
              });
          }
        });

        groupedAssetsRaw.forEach(asset => {
          if (siteDataMap.has(asset.site)) {
            Object.assign(siteDataMap.get(asset.site),
              {
                'ave_risk': asset.ave_risk,
                'assets_count': asset.assets_count
              });
          }
        });

        iemPluginsRaw.forEach(plugin => {
          if (siteDataMap.has(plugin.site)) {
            Object.assign(siteDataMap.get(plugin.site), {'vulnerabilities_count': plugin.count});
          }
        });

        events.forEach(event => {
          if (siteDataMap.has(event.site)) {
            Object.assign(siteDataMap.get(event.site), {'events_count': event.count});
          }
        });

        return Array.from(siteDataMap.values());
      })
    );
  }


  public readonly NAME_LINK: ColDef = {
    field: 'name',
    headerName: this.texts.table_columns.name,
    cellRenderer: 'externalLinkRenderer',
    cellRendererParams: (params: ICellRendererParams): Pick<ExternalLinkICellRendererParams, 'text' | 'link' | 'onClick'> => {
      return {
        'text': params.value ?? params.rowIndex + 1,
        'link': `${RoutesStatesNamesEnum.root}/${params.data.id}/${this.defaultPathService.getDefaultPath}`,
        'onClick': () => this.icpsService.logRemoteConnection(params.data.id),
      };
    },
  };

  public readonly ASSETS: ColDef = {
    headerName: this.texts.table_columns.assets,
    field: 'assets_count',
  };

  public readonly RISK: ColDef = {
    headerName: this.texts.table_columns.risk_score,
    cellRenderer: RiskScoreCellRendererComponent,
    sort: 'desc',
    valueGetter: (params) => params.data.ave_risk,
    keyCreator: params => this.riskService.getRiskModel(params.value).text.display,
    tooltipValueGetter: (params: ITooltipParams): string =>
      +params.value === RISK_OFF_VALUE
        ? this.languageService.texts.general.risk.status.off
        : this.riskService.getRiskModel(params.value).text.display,
  };

  public readonly VULNERABILITIES: ColDef = {
    headerName: this.texts.table_columns.vulnerabilities,
    field: 'vulnerabilities_count',
  };

  public readonly EVENTS: ColDef = {
    headerName: this.texts.table_columns.last_events,
    field: 'events_count',
  };

  public gridOptions: UiGridOptions<ConnectedSiteTableRow> = {
    gridId: UiGridIdsEnum.OVERVIEW_HIGHEST_RISK,
    gridTitle: this.texts.title,
    headerOptions: {
      hideHeader: true,
    },
    suppressColumnPinning: true,
    dataSource: this.getCombinedData(),
    columnDefs: [
      this.NAME_LINK,
      this.ASSETS,
      this.RISK,
      this.VULNERABILITIES,
      this.EVENTS],
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 100,
    },
    components: {
      externalLinkRenderer: ExternalLinkRendererComponent,
    },
    disableSavingGridState: true,
  };

}
